import { call, put, takeLatest, select } from "redux-saga/effects";
import { delay } from "redux-saga";
import { push, replace } from "connected-react-router";
import * as onboarding from "middleware/onboarding";
import * as settings from "middleware/settings";
import * as session from "middleware/session";
import {
    types as onboardingTypes,
    selectors as onboardingSelectors,
    actions as onboardingActions,
} from "reducers/onboarding";
import { selectors as enrollmentSelectors, types as enrollmentTypes } from "reducers/enrollment";
import { selectors as environmentSelectors } from "reducers/environment";
// eslint-disable-next-line no-unused-vars
import { selectors as loginSelectors, actions as loginActions, types as loginTypes } from "reducers/login";
import { adjustIdFieldErrors } from "util/form.js";
import * as i18n from "util/i18n";
import * as config from "util/config";
import { captureBack, captureFront, captureSelfie } from "util/vuOnBoarding";
import {
    AUTHORIZED,
    getCameraAuthorizationStatus,
    NOT_DETERMINED,
    PERMISSION_GRANTED,
    PERMISSION_NOT_REQUESTED,
    switchToSettings,
} from "util/diagnostic";
import { ANDROID_DEVICE, IOS_DEVICE, getMobileOS } from "util/device";
import { confirm } from "util/notification";
import gaEventTracker, { events } from "util/gaEventTracker";
import { actions as notificationActions } from "reducers/notification";

const sagas = [
    takeLatest(onboardingTypes.NEW_OPERATION_VU_REQUEST, generateNewOperationVU),
    takeLatest(onboardingTypes.SCAN_DOCUMENT_REQUEST, scanDocumentRequest),
    takeLatest(onboardingTypes.SCAN_DOCUMENT_REQUEST_BACK, scanDocumentRequestBack),
    takeLatest(onboardingTypes.SCAN_DOCUMENT_REQUEST_MIGRATION, scanDocumentRequestMigration),
    takeLatest(onboardingTypes.CAPTURE_SELFIE_REQUEST, captureSelfieRequest),
    takeLatest(onboardingTypes.CAPTURE_SELFIE_MIGRATION, captureSelfieMigration),
    takeLatest(onboardingTypes.SCAN_DOCUMENT_REQUEST_MIGRATION_BACK, scanDocumentRequestMigrationBack),

    takeLatest(onboardingTypes.TAKE_PICTURE_REQUEST, takePicture),
    takeLatest(onboardingTypes.REQUEST_LIST_STATES, listStates),
    takeLatest(onboardingTypes.REQUEST_LIST_JOBS, listJobs),
    takeLatest(onboardingTypes.REQUEST_LIST_SELECTOR_OPTIONS, listSelectorList),
    takeLatest(onboardingTypes.REQUEST_LIST_OCCUPATIONS, listOccupations),
    takeLatest(onboardingTypes.REQUEST_LIST_PERSONAL_INFO, listPersonalInfo),
    takeLatest(onboardingTypes.REQUEST_LIST_LOCALITY, listLocalities),
    takeLatest(onboardingTypes.REQUEST_SAVE_PERSONAL_DATA, requestSavePersonalData),
    takeLatest(onboardingTypes.REQUEST_GET_PERSONAL_DATA, getPersonalData),

    takeLatest(onboardingTypes.REQUEST_LIST_REGULATORY_INFO, listRegulatoryInfo),
    takeLatest(onboardingTypes.REQUEST_LEGAL_REPRESENTATIVE_INFO, legalRepresentativeSelectors),
    // takeLatest(onboardingTypes.REQUEST_SAVE_REGULATORY_DATA, requestSaveRegulatoryData),

    takeLatest(
        onboardingTypes.UPLOAD_CLIENT_INFO_AND_SEND_INVITATION_CODE_REQUEST,
        uploadClientInfoAndSendInvitationCode,
    ),
    takeLatest(onboardingTypes.UPLOAD_PICTURE_REQUEST, uploadPicture),

    takeLatest(onboardingTypes.REQUEST_VERIFY_USERNAME, verifyUsername),
    takeLatest(onboardingTypes.REQUEST_VERIFY_CUIT, verifyCuit),
    takeLatest(onboardingTypes.REQUEST_VERIFY_CODE_SEND, verifyCodeSend),
    takeLatest(onboardingTypes.REQUEST_VERIFY_CODE_RECEIVE, verifyCodeReceive),
    takeLatest(onboardingTypes.REQUEST_VERIFY_CODE_RECEIVE_MIGRATION, verifyCodeReceiveMigration),

    takeLatest(onboardingTypes.REQUEST_CREATE_USER, requestCreateUser),

    takeLatest(onboardingTypes.REQUEST_ACCEPT_SCHEME, acceptScheme),

    takeLatest(onboardingTypes.USER_ROLE_REQUEST, getUserRole),

    takeLatest(onboardingTypes.NOSIS_CDA_REQUEST, verifyNosisCDA),

    takeLatest(onboardingTypes.ONB_INVITATION_CODE_REQUEST, verifyOnbInvitationCode),

    takeLatest(onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_REQUEST, handleVerifyUserCredentialsStep1Request),

    takeLatest(onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_REQUEST, handleVerifyUserCredentialsStep2Request),

    takeLatest(onboardingTypes.ENROLLED_USER_SELECT_ROLE_REQUEST, handleEnrolledUserSelectRole),

    takeLatest(onboardingTypes.REQUEST_MIGRATION_UPDATE_USER, requestUpdateMigratedUser),

    takeLatest(onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_PF_REQUEST, requestAcceptTermsAndConditionsPF),

    takeLatest(onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_PJ_REQUEST, requestAcceptTermsAndConditionsPJ),
    takeLatest(
        onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_MIGRATION_REQUEST,
        handleVerifyUserCredentialsStep1MigrationRequest,
    ),
    takeLatest(
        onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_MIGRATION_REQUEST,
        handleVerifyUserCredentialsStep1MigrationRequest,
    ),

    takeLatest(
        onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_MIGRATION_REQUEST,
        handleVerifyUserCredentialsStep2MigrationRequest,
    ),
    takeLatest(onboardingTypes.MIGRATION_SAVE_PERSONAL_DATA_REQUEST, requestMigrationSavePersonalData),
];

export default sagas;
const errorStep = {
    documentBack: "step2/back",
    documentFront: "step2/front",
    passport: "step2",
    selfie: "step3",
};

const nextStep = {
    documentBack: "step3",
    documentFront: "step2/back",
    passport: "step3",
    selfie: "step4",
};

const VU_ONBOARDING_INVALID_END_OPERATION = "BAK608W";

function* takePicture({ currentStep, documentSide, picture, exchangeToken }) {
    yield put({ type: onboardingTypes.TAKE_PICTURE_SUCCESS, picture, exchangeToken });
    yield put(push(`/onboarding/${currentStep}${documentSide ? `/${documentSide}` : ""}/processing`));
}

function* listStates() {
    const response = yield call(onboarding.listStates);

    if (response) {
        if (response.type === "W") {
            const errors = adjustIdFieldErrors(response.data.data);

            yield put({
                type: onboardingTypes.FAILURE_LIST_STATES,
                errors,
            });
            yield put(push(`/onboarding/step5/error`));
        } else {
            const { states } = response.data.data;

            yield put({
                type: onboardingTypes.SUCCESS_LIST_STATES,
                states,
            });
        }
    }
}

function* listJobs() {
    const response = yield call(onboarding.listJobs);

    if (response) {
        if (response.type === "W") {
            const errors = adjustIdFieldErrors(response.data.data);

            yield put({
                type: onboardingTypes.FAILURE_LIST_JOBS,
                errors,
            });
            yield put(push(`/onboarding/step6/error`));
        } else {
            const { jobs } = response.data.data;

            yield put({
                type: onboardingTypes.SUCCESS_LIST_JOBS,
                jobs,
            });
        }
    }
}

function* listSelectorList() {
    const response = yield call(onboarding.listSelectorList);

    if (response) {
        if (response.type === "W") {
            const errors = adjustIdFieldErrors(response.data.data);

            yield put({
                type: onboardingTypes.FAILURE_LIST_OCCUPATIONS,
                errors,
            });
            yield put(push(`/onboarding/step6/error`));
        } else {
            const {
                occupation_options,
                source_of_wealth_options,
                annual_income_options,
                countryList,
            } = response.data.data;

            yield put({
                type: onboardingTypes.SUCCESS_LIST_OCCUPATIONS,
                occupation_options,
                source_of_wealth_options,
                annual_income_options,
                countryList,
            });
        }
    }
}

function* listOccupations() {
    const response = yield call(onboarding.listOccupations);

    if (response) {
        if (response.type === "W") {
            const errors = adjustIdFieldErrors(response.data.data);

            yield put({
                type: onboardingTypes.FAILURE_LIST_OCCUPATIONS,
                errors,
            });
            yield put(push(`/onboarding/step6/error`));
        } else {
            const { occupations } = response.data.data;
            yield put({
                type: onboardingTypes.SUCCESS_LIST_OCCUPATIONS,
                occupations,
            });
        }
    }
}

function* listPersonalInfo({ guestFlow }) {
    const response = yield call(onboarding.listPersonalInfo, { guestFlow });

    if (response) {
        if (response.type === "W") {
            yield put({
                type: onboardingTypes.FAILURE_LIST_PERSONAL_INFO,
                errors: response.data.data,
            });
        } else {
            const {
                companyRoleSelector,
                maritalStatusSelector,
                occupationSelector,
                countrySelector,
                provinceSelector,
                foreignCountries,
                nationalities,
                genders,
            } = response.data.data;
            yield put({
                type: onboardingTypes.SUCCESS_LIST_PERSONAL_INFO,
                companyRoleList: companyRoleSelector,
                maritalStatusList: maritalStatusSelector,
                occupationList: occupationSelector,
                countryList: countrySelector,
                stateList: provinceSelector,
                nationalities,
                genders,
                foreignCountries,
            });
        }
    }
}

function* listLocalities({ countryCode, provinceCode }) {
    const response = yield call(onboarding.listLocalities, { countryCode, provinceCode });

    if (response) {
        if (response.type === "W") {
            yield put({
                type: onboardingTypes.FAILURE_LIST_LOCALITY,
                errors: response.data.data,
            });
        } else {
            const { localityList } = response.data.data;
            yield put({
                type: onboardingTypes.SUCCESS_LIST_LOCALITY,
                localityList,
            });
        }
    }
}

function* listRegulatoryInfo() {
    const response = yield call(onboarding.listRegulatoryInfo);

    if (response) {
        if (response.type === "W") {
            yield put({
                type: onboardingTypes.FAILURE_LIST_REGULATORY_INFO,
                errors: response.data.data,
            });
        } else {
            const { pepJobsSelector, pepRelationshipTypesSelector, countrySelector } = response.data.data;
            yield put({
                type: onboardingTypes.SUCCESS_LIST_REGULATORY_INFO,
                pepJobsList: pepJobsSelector,
                pepRelationshipTypesList: pepRelationshipTypesSelector,
                countryList: countrySelector,
            });
        }
    }
}
function* legalRepresentativeSelectors() {
    const response = yield call(onboarding.legalRepresentativeSelectors);

    if (response) {
        if (response.type === "W") {
            yield put({
                type: onboardingTypes.FAILURE_LEGAL_REPRESENTATIVE_INFO,
                errors: response.data.data,
            });
        } else {
            const { onboardingUser, cuit } = response.data.data;
            yield put({
                type: onboardingTypes.SUCCESS_LEGAL_REPRESENTATIVE_INFO,
                onboardingUser: onboardingUser ?? { documentNumber: "", user: "" },
                cuit,
            });
        }
    }
}
function* uploadClientInfoAndSendInvitationCode({
    documentNumber,
    email,
    firstName,
    formikBag,
    lastName,
    mobileNumber,
    address,
    jobInformation,
    SSNID,
    region,
}) {
    const { exchangeToken } = formikBag.props;
    try {
        const response = yield call(
            onboarding.uploadClientInfo,
            documentNumber,
            email,
            firstName,
            lastName,
            mobileNumber,
            address,
            jobInformation,
            exchangeToken,
            SSNID,
            region,
        );

        if (response) {
            if (response.type === "W") {
                const errors = adjustIdFieldErrors(response.data.data);

                yield put({
                    type: onboardingTypes.UPLOAD_CLIENT_INFO_AND_SEND_INVITATION_CODE_ERROR,
                    errors,
                });
                yield put(push(`/onboarding/step4/error`));
                // yield put(push("/onboarding/EmailValidation"));
            } else if (response.status === 200) {
                yield put({
                    type: onboardingTypes.UPLOAD_CLIENT_INFO_AND_SEND_INVITATION_CODE_SUCCESS,
                    ...response.data.data,
                });

                // yield put(push("/onboarding/success"));
                yield put(push("/enrollment"));
            }
        }

        if (formikBag) {
            formikBag.setSubmitting(false);
        }
    } catch (error) {
        if (error.data.code === "API507E") {
            yield put(push("/onboarding/invitation/fail"));
            // yield put(push("/onboarding/EmailValidation"));
        } else {
            throw error;
        }
    }
}

function* uploadPicture({ documentType, exchangeToken, picture, rotatePicture }) {
    try {
        const response = yield call(onboarding.uploadPicture, documentType, picture, exchangeToken, rotatePicture);

        if (response && response.status === 200) {
            const { _exchangeToken, ...rest } = response.data.data;

            if (response.type === "W") {
                const errors = adjustIdFieldErrors(rest);

                yield put({
                    type: onboardingTypes.UPLOAD_PICTURE_ERROR,
                    errors,
                    exchangeToken: _exchangeToken || exchangeToken,
                });
                yield put(replace(`/onboarding/${errorStep[documentType]}/error`));
            } else {
                if (documentType === "documentBack" || documentType === "passport") {
                    const { documentCountry, documentNumber, firstName, fullDocument, lastName, dateOfBirth } = rest;
                    yield put({
                        type: onboardingTypes.SET_DOCUMENT_DATA,
                        documentCountry,
                        documentNumber,
                        firstName,
                        fullDocument,
                        lastName,
                        dateOfBirth,
                    });
                }

                yield put({
                    type: onboardingTypes.UPLOAD_PICTURE_SUCCESS,
                    exchangeToken: _exchangeToken,
                    ...rest,
                });

                if (rest.skipBackDocumentStep) {
                    const { documentCountry, documentNumber, firstName, fullDocument, lastName, dateOfBirth } = rest;
                    yield put({
                        type: onboardingTypes.SET_DOCUMENT_DATA,
                        documentCountry,
                        documentNumber,
                        firstName,
                        fullDocument,
                        lastName,
                        dateOfBirth,
                    });
                    yield put(replace(`/onboarding/${nextStep.documentBack}`));
                } else {
                    yield put(replace(`/onboarding/${nextStep[documentType]}`));
                }
            }
        }
    } catch (error) {
        const errors = adjustIdFieldErrors(error);
        yield put({ type: onboardingTypes.UPLOAD_PICTURE_ERROR, errors });

        yield put(replace(`/onboarding/${errorStep[documentType]}/error`));
    }
}

function* verifyUsername({ username }) {
    const params = { _userConfirmation: username };
    const response = yield call(onboarding.verifyUsername, params);

    if (response && response.status === 200) {
        const { code } = response.data;
        const { isUsernameAvailable } = response.data?.data;

        if (code && code === "COR000I" && !isUsernameAvailable) {
            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_USERNAME,
                isUsernameAvailable: "USERNAME_UNAVAILABLE",
                username: "",
            });
        } else if (code && code === "COR000I" && !!isUsernameAvailable) {
            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_USERNAME,
                isUsernameAvailable: "USERNAME_AVAILABLE",
                username,
            });
        } else {
            yield put({ type: onboardingTypes.FAILURE_VERIFY_USERNAME, isUsernameAvailable: code });
        }
    }
}

function* verifyCuit({ cuit }) {
    const params = { cuit };
    const response = yield call(onboarding.verifyCuitBibank, params);
    if (response && response.status === 200) {
        const { code } = response.data;
        const { isAlreadyCreated, _exchangeToken, cuit: message } = response.data?.data;

        if (_exchangeToken) {
            yield put({
                type: "enrollment/SAVE_EXCHANGE_TOKEN",
                exchangeToken: _exchangeToken,
            });
        }

        if (code && code === "COR000I" && !isAlreadyCreated) {
            // AFIP
            yield* verifyCuitAfip({ cuit, _exchangeToken });
        } else if (code && code === "COR020W") {
            gaEventTracker.triggerEvent(events.existingClient);

            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CUIT,
                business: { status: "CUIT_IN_BIBANK", message },
                exchangeToken: _exchangeToken,
            });
        } else {
            yield put({ type: onboardingTypes.FAILURE_VERIFY_CUIT });
        }
    }
}

function* verifyCuitAfip({ cuit, _exchangeToken }) {
    const params = { cuit };

    const response = yield call(onboarding.verifyCuitAfip, params, _exchangeToken);

    if (response && response.status === 200) {
        const { code = i18n.get("global.unexpectedError") } = response.data;

        const ticketStates = {
            API550W: "CUIT_NOT_IN_AFIP",
            API551W: "CUIT_NOT_REGISTERED_RESPONSIBLE",
            COR000I: "CUIT_VALID",
            API553W: "VU_NEW_OPERATION_ERROR",
        };

        if (code && ticketStates[code] === "CUIT_VALID") {
            gaEventTracker.triggerEvent(events.registry3);

            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CUIT,
                business: {
                    status: "CUIT_VALID",
                    data: response.data.data,
                    name: response.data.data.business.businessName,
                },
            });
        } else if (code && ticketStates[code] === "CUIT_NOT_IN_AFIP") {
            gaEventTracker.triggerEvent(events.invalidCUIT("CUIT inválido"));

            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CUIT,
                business: { status: "CUIT_NOT_IN_AFIP" },
            });
        } else if (code && ticketStates[code] === "CUIT_NOT_REGISTERED_RESPONSIBLE") {
            gaEventTracker.triggerEvent(events.invalidCUIT("CUIT inválido"));

            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CUIT,
                business: { status: "CUIT_NOT_REGISTERED_RESPONSIBLE" },
            });
        } else if (code && ticketStates[code] === "VU_NEW_OPERATION_ERROR") {
            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CUIT,
                business: { status: "VU_NEW_OPERATION_ERROR" },
            });
        } else {
            yield put({ type: onboardingTypes.FAILURE_VERIFY_CUIT });
        }
    }
}

function* generateNewOperationVU({ exchangeToken }) {
    const _idTransaction = null;
    const response = yield call(onboarding.generateNewOperationVU, _idTransaction, exchangeToken);
    const unexpectedErrorMessage = i18n.get("global.unexpectedError");

    if (response && response.status === 200) {
        const { code } = response.data;
        if (code && code !== "COR000I") {
            const message = response.data.data?.NEW_OPERATION_ERROR || null;
            yield put({
                type: onboardingTypes.NEW_OPERATION_VU_FAILURE,
                errorValidateIdentityPermissions: false,
                errorValidateIdentity: message || unexpectedErrorMessage,
            });
        } else {
            yield put({ type: onboardingTypes.NEW_OPERATION_VU_SUCCESS });
        }
    } else {
        yield put({
            type: onboardingTypes.NEW_OPERATION_VU_FAILURE,
            errorValidateIdentityPermissions: false,
            errorValidateIdentity: unexpectedErrorMessage,
        });
    }
}

function* checkCameraPermission() {
    const permissionStatus = yield call(getCameraAuthorizationStatus);
    console.log("permissionStatus ", permissionStatus);

    let hasCameraPermission = false;
    if (getMobileOS() === ANDROID_DEVICE) {
        hasCameraPermission = permissionStatus === PERMISSION_GRANTED || permissionStatus === PERMISSION_NOT_REQUESTED;
    } else if (getMobileOS() === IOS_DEVICE) {
        hasCameraPermission = permissionStatus === AUTHORIZED || permissionStatus === NOT_DETERMINED;
    }
    return hasCameraPermission;
}

function* doCaptureFront() {
    const maxAttempts = config.getInteger("onBoarding.validateDocument.attempts", 3);

    let captureFrontAttempts = 0;
    let continueCaptureFront = true;
    let maxAttemptsFrontReached = false;
    let isCanceledByUserFront = false;
    let documentFrontImage;

    do {
        try {
            console.log("doCaptureFront - start");
            const captureFrontResult = yield captureFront();
            console.log("doCaptureFront - captureFrontResult: ", captureFrontResult);
            documentFrontImage = captureFrontResult.imageBase64;
            continueCaptureFront = false;
        } catch (error) {
            console.log("doCaptureFront - error: ", error);
            isCanceledByUserFront =
                error.errorCode === "USER_CANCELLED" ||
                error.errorCode === "BACKGROUND_TASKING_ERROR" ||
                error.errorCode === "CAMERA_PERMISSION_DENIED" ||
                error.errorCode === "DOCUMENT_FACE_NOT_FOUND";
            maxAttemptsFrontReached = captureFrontAttempts >= maxAttempts || isCanceledByUserFront;

            if (maxAttemptsFrontReached) {
                continueCaptureFront = false;
            } else {
                captureFrontAttempts += 1;
            }
        }
    } while (continueCaptureFront);

    return { documentFrontImage, maxAttemptsFrontReached };
}

// Escaneo de dorso con reintentos
function* doCaptureBack() {
    const maxAttempts = config.getInteger("onBoarding.validateDocument.attempts", 3);

    let captureBackAttempts = 0;
    let continueCaptureBack = true;
    let maxAttemptsBackReached = false;
    let isCanceledByUserBack = false;
    let documentBackImage;

    do {
        try {
            console.log("doCaptureBack - start");
            const captureBackResult = yield captureBack();
            console.log("doCaptureBack - captureBackResult: ", captureBackResult);
            documentBackImage = captureBackResult.imageBase64;
            continueCaptureBack = false;
        } catch (error) {
            console.log("doCaptureBack - error: ", error);
            isCanceledByUserBack =
                error.errorCode === "USER_CANCELLED" ||
                error.errorCode === "BACKGROUND_TASKING_ERROR" ||
                error.errorCode === "CAMERA_PERMISSION_DENIED";
            maxAttemptsBackReached = captureBackAttempts >= maxAttempts || isCanceledByUserBack;

            if (maxAttemptsBackReached) {
                continueCaptureBack = false;
            } else {
                captureBackAttempts += 1;
            }
        }
    } while (continueCaptureBack);

    return { documentBackImage, maxAttemptsBackReached };
}

// Validacion del documento en API con VU con reintentos
function* doValidateDocument(documentFrontImage, documentBackImage, exchangeToken, guestFlow = false) {
    const maxAttempts = config.getInteger("onBoarding.validateDocument.attempts", 3);

    let continueValidateDocument = true;
    let validateDocumentAttempts = 0;
    let maxAttemptsValidateDocumentReached = false;
    let validateResponse;
    let _idTransaction = null;

    do {
        try {
            validateResponse = yield call(
                onboarding.validateDocument,
                documentFrontImage,
                documentBackImage,
                _idTransaction,
                exchangeToken,
                guestFlow,
            );

            if (validateResponse.type === "W") {
                const hasValidationError = validateResponse.data?.code === "COR020W";

                const isValidToRetry = validateResponse.data?.data?.isValidToRetry || false;

                maxAttemptsValidateDocumentReached = validateDocumentAttempts >= maxAttempts;

                if (maxAttemptsValidateDocumentReached || hasValidationError || !isValidToRetry) {
                    continueValidateDocument = false;
                } else {
                    validateDocumentAttempts += 1;
                    const { idTransaction } = validateResponse.data;
                    _idTransaction = idTransaction;
                }
            } else {
                continueValidateDocument = false;
            }
        } catch (error) {
            const isServiceError = error.data?.code === "BAK004E";
            maxAttemptsValidateDocumentReached = validateDocumentAttempts >= maxAttempts || !isServiceError;

            if (maxAttemptsValidateDocumentReached) {
                return error;
            }
            validateDocumentAttempts += 1;
            const { idTransaction } = validateResponse.data;
            _idTransaction = idTransaction;
        }
    } while (continueValidateDocument);

    return validateResponse;
}

function* doValidateDocumentMigration(documentFrontImage, documentBackImage, exchangeToken, guestFlow = false) {
    const maxAttempts = config.getInteger("onBoarding.validateDocument.attempts", 3);

    let continueValidateDocument = true;
    let validateDocumentAttempts = 0;
    let maxAttemptsValidateDocumentReached = false;
    let validateResponse;
    let _idTransaction = null;

    do {
        try {
            validateResponse = yield call(
                onboarding.validateDocumentMigration,
                documentFrontImage,
                documentBackImage,
                _idTransaction,
                exchangeToken,
                guestFlow,
            );

            if (validateResponse.type === "W") {
                const hasValidationError = validateResponse.data?.code === "COR020W";

                const isValidToRetry = validateResponse.data?.data?.isValidToRetry || false;

                maxAttemptsValidateDocumentReached = validateDocumentAttempts >= maxAttempts;

                if (maxAttemptsValidateDocumentReached || hasValidationError || !isValidToRetry) {
                    continueValidateDocument = false;
                } else {
                    validateDocumentAttempts += 1;
                    const { idTransaction } = validateResponse.data;
                    _idTransaction = idTransaction;
                }
            } else {
                continueValidateDocument = false;
            }
        } catch (error) {
            const isServiceError = error.data?.code === "BAK004E";
            maxAttemptsValidateDocumentReached = validateDocumentAttempts >= maxAttempts || !isServiceError;

            if (maxAttemptsValidateDocumentReached) {
                return error;
            }
            validateDocumentAttempts += 1;
            const { idTransaction } = validateResponse.data;
            _idTransaction = idTransaction;
        }
    } while (continueValidateDocument);

    return validateResponse;
}

// Escanear y Validar CI
function* scanDocumentRequest({ exchangeToken, guestFlow = false }) {
    console.log("scanDocumentRequest start");
    const scanDocumentErrorMessage = i18n.get("onboarding.captureDocument.invalid");
    const hasCameraPermission = yield* checkCameraPermission();
    console.log("hasCameraPermission ", hasCameraPermission);
    if (hasCameraPermission) {
        // Escaneo de frente con reintentos
        console.log("doCaptureFront start");
        const doCaptureFrontData = yield* doCaptureFront();
        console.log("doCaptureFrontData ", doCaptureFrontData);
        const { documentFrontImage, maxAttemptsFrontReached } = doCaptureFrontData;
        console.log("maxAttemptsFrontReached ", maxAttemptsFrontReached);
        const unexpectedErrorMessage = i18n.get("global.unexpectedError");
        if (maxAttemptsFrontReached) {
            yield put({
                type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                errorValidateIdentityPermissions: true,
                errorValidateIdentity: scanDocumentErrorMessage,
            });
        } else {
            // Escaneo de dorso con reintentos
            // const doCaptureBackData = yield* doCaptureBack();
            // const { documentBackImage, maxAttemptsBackReached } = doCaptureBackData;
            // yield put({
            //     type: onboardingTypes.CLEAN_SCAN_DOCUMENT_FLAG,
            // });
            // if (maxAttemptsBackReached) {
            //     yield put({
            //         type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            //         errorValidateIdentityPermissions: false,
            //         errorValidateIdentity: scanDocumentErrorMessage,
            //     });
            // } else {
            //     // Validacion del documento en API con VU con reintentos
            //     const validateResponse = yield* doValidateDocument(
            //         documentFrontImage,
            //         documentBackImage,
            //         exchangeToken,
            //         guestFlow,
            //     );

            //     if (
            //         validateResponse.type === "W" ||
            //         validateResponse.type === "E" ||
            //         validateResponse.status !== 200 ||
            //         !validateResponse.type
            //     ) {
            //         if (validateResponse.data.code === "API607W") {
            //             yield put({
            //                 type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            //                 errorValidateIdentityPermissions: false,
            //                 errorValidateIdentity: i18n.get(
            //                     "returnCode.API607W",
            //                     "Ya eres usuario en BiBank, por favor realiza el registro desde la opción Registrar otra empresa",
            //                 ),
            //             });
            //         } else {
            //             const messageNoField = validateResponse.data.data?.NO_FIELD || null;
            //             const message = validateResponse.data.data?.message || null;
            //             yield put({
            //                 type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            //                 errorValidateIdentityPermissions: false,
            //                 errorValidateIdentity: messageNoField || message || unexpectedErrorMessage,
            //             });
            //         }
            //     } else {
            //         const {
            //             documentNumber,
            //             lastName,
            //             firstName,
            //             address,
            //             gender,
            //             cuil,
            //         } = validateResponse.data.data?.documentData;
            yield put({
                type: onboardingTypes.SCAN_DOCUMENT_SUCCESS_FRONT,
                documentFrontImage,
            });
            yield put(push("/onboarding/stepScanDocumentBack"));
            // }
            // }
        }
    } else {
        yield put({
            type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            errorValidateIdentityPermissions: true,
            errorValidateIdentity: "scanDocumentErrorMessage",
        });
        // Si no fueron otorgados los permisos sobre la camara se muestra el dialog
        // consultando para que sean otorgados con posibilidad de ir al settings.
        const confirmValue = yield call(
            confirm,
            i18n.get(
                "vuOnBoarding.scanDocument.cameraPermission.message",
                "Para poder continuar, debe de otorgar permisos de foto y video.",
            ),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.ok", "De acuerdo"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.cancel", "Cancelar"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.title", "Otorgar permisos"),
        );
        if (confirmValue === 1) {
            yield call(switchToSettings);
        }
        // Se comento porque en el figma no ponen captura de notificacion para mobile, al rechazar permiso de camara
    }
}

// Escanear y Validar CI Back
function* scanDocumentRequestBack({ exchangeToken, guestFlow = false }) {
    const scanDocumentErrorMessage = i18n.get("onboarding.captureDocument.invalid");
    const hasCameraPermission = yield* checkCameraPermission();
    if (hasCameraPermission) {
        const unexpectedErrorMessage = i18n.get("global.unexpectedError");

        // Escaneo de dorso con reintentos
        const doCaptureBackData = yield* doCaptureBack();
        const { documentBackImage, maxAttemptsBackReached } = doCaptureBackData;
        // yield put({
        //     type: onboardingTypes.CLEAN_SCAN_DOCUMENT_FLAG,
        // });
        if (maxAttemptsBackReached) {
            yield put({
                type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                errorValidateIdentityPermissions: false,
                errorValidateIdentity: scanDocumentErrorMessage,
            });
        } else {
            // Validacion del documento en API con VU con reintentos
            const documentFrontImage = yield select(onboardingSelectors.getDocumentFrontImage);
            const validateResponse = yield* doValidateDocument(
                documentFrontImage,
                documentBackImage,
                exchangeToken,
                guestFlow,
            );

            if (
                validateResponse.type === "W" ||
                validateResponse.type === "E" ||
                validateResponse.status !== 200 ||
                !validateResponse.type
            ) {
                if (validateResponse.data.code === "API607W") {
                    yield put({
                        type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                        errorValidateIdentityPermissions: false,
                        errorValidateIdentity: i18n.get(
                            "returnCode.API607W",
                            "Ya eres usuario en BiBank, por favor realiza el registro desde la opción Registrar otra empresa",
                        ),
                    });
                } else {
                    const messageNoField = validateResponse.data.data?.NO_FIELD || null;
                    const message = validateResponse.data.data?.message || null;
                    yield put({
                        type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                        errorValidateIdentityPermissions: false,
                        errorValidateIdentity: messageNoField || message || unexpectedErrorMessage,
                    });
                }
            } else {
                const {
                    documentNumber,
                    lastName,
                    firstName,
                    address,
                    gender,
                    cuil,
                } = validateResponse.data.data?.documentData;
                yield put({
                    type: onboardingTypes.SCAN_DOCUMENT_SUCCESS,
                    documentNumber,
                    lastName,
                    firstName,
                    address,
                    gender,
                    cuil,
                    documentFrontImage,
                    documentBackImage,
                });
                yield put(push("/onboarding/StepScanDocumentSuccess"));
            }
        }
    } else {
        yield put({
            type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            errorValidateIdentityPermissions: true,
            errorValidateIdentity: "scanDocumentErrorMessage",
        });
        // Si no fueron otorgados los permisos sobre la camara se muestra el dialog
        // consultando para que sean otorgados con posibilidad de ir al settings.
        const confirmValue = yield call(
            confirm,
            i18n.get(
                "vuOnBoarding.scanDocument.cameraPermission.message",
                "Para poder continuar, debe de otorgar permisos de foto y video.",
            ),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.ok", "De acuerdo"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.cancel", "Cancelar"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.title", "Otorgar permisos"),
        );
        if (confirmValue === 1) {
            yield call(switchToSettings);
        }
        // Se comento porque en el figma no ponen captura de notificacion para mobile, al rechazar permiso de camara
    }
}

function* scanDocumentRequestMigration({ exchangeToken, guestFlow = false }) {
    const scanDocumentErrorMessage = i18n.get("onboarding.captureDocument.invalid");
    const hasCameraPermission = yield* checkCameraPermission();
    if (hasCameraPermission) {
        // Escaneo de frente con reintentos
        const doCaptureFrontData = yield* doCaptureFront();
        const { documentFrontImage, maxAttemptsFrontReached } = doCaptureFrontData;
        const unexpectedErrorMessage = i18n.get("global.unexpectedError");
        if (maxAttemptsFrontReached) {
            yield put({
                type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                errorValidateIdentityPermissions: true,
                errorValidateIdentity: scanDocumentErrorMessage,
            });
        } else {
            // Escaneo de dorso con reintentos
            // const doCaptureBackData = yield* doCaptureBack();
            // const { documentBackImage, maxAttemptsBackReached } = doCaptureBackData;
            // yield put({
            //     type: onboardingTypes.CLEAN_SCAN_DOCUMENT_FLAG,
            // });
            // if (maxAttemptsBackReached) {
            //     yield put({
            //         type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            //         errorValidateIdentityPermissions: false,
            //         errorValidateIdentity: scanDocumentErrorMessage,
            //     });
            // } else {
            //     // Validacion del documento en API con VU con reintentos
            //     const validateResponse = yield* doValidateDocumentMigration(
            //         documentFrontImage,
            //         documentBackImage,
            //         exchangeToken,
            //         guestFlow,
            //     );

            //     if (
            //         validateResponse.type === "W" ||
            //         validateResponse.type === "E" ||
            //         validateResponse.status !== 200 ||
            //         !validateResponse.type
            //     ) {
            //         if (validateResponse.data.code === "API607W") {
            //             yield put({
            //                 type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            //                 errorValidateIdentityPermissions: false,
            //                 errorValidateIdentity: i18n.get(
            //                     "returnCode.API607W",
            //                     "Ya eres usuario en BiBank, por favor realiza el registro desde la opción Registrar otra empresa",
            //                 ),
            //             });
            //         } else {
            //             const messageNoField = validateResponse.data.data?.NO_FIELD || null;
            //             const message = validateResponse.data.data?.message || null;
            //             yield put({
            //                 type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            //                 errorValidateIdentityPermissions: false,
            //                 errorValidateIdentity: messageNoField || message || unexpectedErrorMessage,
            //             });
            //         }
            //     } else {
            //         const {
            //             documentNumber,
            //             lastName,
            //             firstName,
            //             address,
            //             gender,
            //             cuil,
            //         } = validateResponse.data.data?.documentData;
            yield put({
                type: onboardingTypes.SCAN_DOCUMENT_SUCCESS_FRONT,
                documentFrontImage,
            });
            yield put(push("/migration/StepScanDocumentBack"));
            // }
            // }
        }
    } else {
        yield put({
            type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            errorValidateIdentityPermissions: true,
            errorValidateIdentity: "scanDocumentErrorMessage",
        });
        // Si no fueron otorgados los permisos sobre la camara se muestra el dialog
        // consultando para que sean otorgados con posibilidad de ir al settings.
        const confirmValue = yield call(
            confirm,
            i18n.get(
                "vuOnBoarding.scanDocument.cameraPermission.message",
                "Para poder continuar, debe de otorgar permisos de foto y video.",
            ),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.ok", "De acuerdo"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.cancel", "Cancelar"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.title", "Otorgar permisos"),
        );
        if (confirmValue === 1) {
            yield call(switchToSettings);
        }
        // Se comento porque en el figma no ponen captura de notificacion para mobile, al rechazar permiso de camara
    }
}

function* scanDocumentRequestMigrationBack({ exchangeToken, guestFlow = false }) {
    const scanDocumentErrorMessage = i18n.get("onboarding.captureDocument.invalid");
    const hasCameraPermission = yield* checkCameraPermission();
    if (hasCameraPermission) {
        const unexpectedErrorMessage = i18n.get("global.unexpectedError");

        // Escaneo de dorso con reintentos
        const doCaptureBackData = yield* doCaptureBack();
        const { documentBackImage, maxAttemptsBackReached } = doCaptureBackData;
        // yield put({
        //     type: onboardingTypes.CLEAN_SCAN_DOCUMENT_FLAG,
        // });
        if (maxAttemptsBackReached) {
            yield put({
                type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                errorValidateIdentityPermissions: false,
                errorValidateIdentity: scanDocumentErrorMessage,
            });
        } else {
            // Validacion del documento en API con VU con reintentos
            const documentFrontImage = yield select(onboardingSelectors.getDocumentFrontImage);

            const validateResponse = yield* doValidateDocumentMigration(
                documentFrontImage,
                documentBackImage,
                exchangeToken,
                guestFlow,
            );

            if (
                validateResponse.type === "W" ||
                validateResponse.type === "E" ||
                validateResponse.status !== 200 ||
                !validateResponse.type
            ) {
                if (validateResponse.data.code === "API607W") {
                    yield put({
                        type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                        errorValidateIdentityPermissions: false,
                        errorValidateIdentity: i18n.get(
                            "returnCode.API607W",
                            "Ya eres usuario en BiBank, por favor realiza el registro desde la opción Registrar otra empresa",
                        ),
                    });
                } else {
                    const messageNoField = validateResponse.data.data?.NO_FIELD || null;
                    const message = validateResponse.data.data?.message || null;
                    yield put({
                        type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
                        errorValidateIdentityPermissions: false,
                        errorValidateIdentity: messageNoField || message || unexpectedErrorMessage,
                    });
                }
            } else {
                const {
                    documentNumber,
                    lastName,
                    firstName,
                    address,
                    gender,
                    cuil,
                } = validateResponse.data.data?.documentData;
                yield put({
                    type: onboardingTypes.SCAN_DOCUMENT_SUCCESS,
                    documentNumber,
                    lastName,
                    firstName,
                    address,
                    gender,
                    cuil,
                });
                yield put(push("/migration/StepScanDocumentSuccess"));
            }
        }
    } else {
        yield put({
            type: onboardingTypes.SCAN_DOCUMENT_FAILURE,
            errorValidateIdentityPermissions: true,
            errorValidateIdentity: "scanDocumentErrorMessage",
        });
        // Si no fueron otorgados los permisos sobre la camara se muestra el dialog
        // consultando para que sean otorgados con posibilidad de ir al settings.
        const confirmValue = yield call(
            confirm,
            i18n.get(
                "vuOnBoarding.scanDocument.cameraPermission.message",
                "Para poder continuar, debe de otorgar permisos de foto y video.",
            ),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.ok", "De acuerdo"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.cancel", "Cancelar"),
            i18n.get("vuOnBoarding.scanDocument.cameraPermission.title", "Otorgar permisos"),
        );
        if (confirmValue === 1) {
            yield call(switchToSettings);
        }
        // Se comento porque en el figma no ponen captura de notificacion para mobile, al rechazar permiso de camara
    }
}

function* doCaptureSelfie() {
    const maxAttempts = config.getInteger("onboarding.validateSelfie.attempts", 3);
    const minScore = config.getInteger("onboarding.validateSelfie.minScore", 100);

    let captureAttempts = 0;
    let continueCapture = true;
    let maxAttemptsReached = false;
    let isCanceledByUser = false;
    let hasImageError = false;
    let selfieList;
    let analysisSelfieList;

    do {
        try {
            console.log("doCaptureSelfie - start");
            const captureSelfieResult = yield captureSelfie(minScore);
            console.log("doCaptureSelfie - captureSelfieResult: ", captureSelfieResult);
            selfieList = captureSelfieResult.selfieList && JSON.stringify(captureSelfieResult.selfieList);
            analysisSelfieList =
                captureSelfieResult.analysisSelfieList && JSON.stringify(captureSelfieResult.analysisSelfieList);
            continueCapture = false;
        } catch (error) {
            console.log("doCaptureSelfie - error: ", error);
            isCanceledByUser = error.errorCode === "USER_CANCELLED" || error.errorCode === "CAMERA_PERMISSION_DENIED";
            hasImageError = error.errorCode === "TEST_FAILED";
            maxAttemptsReached = captureAttempts >= maxAttempts || isCanceledByUser;

            if (maxAttemptsReached) {
                continueCapture = false;
            } else {
                captureAttempts += 1;
            }
        }
    } while (continueCapture);

    return { maxAttemptsReached, hasImageError, selfieList, analysisSelfieList };
}

// Validacion de la selfie en API con VU con reintentos
function* doValidateSelfie(selfieList, analysisSelfieList, document, gender, exchangeToken, guestFlow = false) {
    const maxAttempts = config.getInteger("onboarding.validateSelfie.attempts", 3);
    const _documentFrontImage = yield select(onboardingSelectors.getDocumentFrontImage);
    const _documentBackImage = yield select(onboardingSelectors.getDocumentBackImage);

    let continueValidate = true;
    let validateAttempts = 0;
    let maxAttemptsValidateReached = false;
    let validateResponse;
    let _idTransaction = null;

    do {
        try {
            validateResponse = yield call(
                onboarding.validateSelfie,
                _documentFrontImage,
                _documentBackImage,
                selfieList,
                analysisSelfieList,
                _idTransaction,
                document,
                gender,
                exchangeToken,
                guestFlow,
            );

            continueValidate = false;
        } catch (error) {
            const isServiceError = error.data?.code === "BAK004E";
            maxAttemptsValidateReached = validateAttempts >= maxAttempts || !isServiceError;
            if (maxAttemptsValidateReached) {
                return error;
            }
            validateAttempts += 1;
            const { idTransaction } = validateResponse.data;
            _idTransaction = idTransaction;
        }
    } while (continueValidate);

    return validateResponse;
}

function* doValidateSelfieMigration(
    selfieList,
    analysisSelfieList,
    document,
    gender,
    exchangeToken,
    guestFlow = false,
) {
    const maxAttempts = config.getInteger("onboarding.validateSelfie.attempts", 3);

    let continueValidate = true;
    let validateAttempts = 0;
    let maxAttemptsValidateReached = false;
    let validateResponse;
    let _idTransaction = null;

    do {
        try {
            validateResponse = yield call(
                onboarding.validateSelfieMigration,
                selfieList,
                analysisSelfieList,
                _idTransaction,
                document,
                gender,
                exchangeToken,
                guestFlow,
            );

            continueValidate = false;
        } catch (error) {
            const isServiceError = error.data?.code === "BAK004E";
            maxAttemptsValidateReached = validateAttempts >= maxAttempts || !isServiceError;
            if (maxAttemptsValidateReached) {
                return error;
            }
            validateAttempts += 1;
            const { idTransaction } = validateResponse.data;
            _idTransaction = idTransaction;
        }
    } while (continueValidate);

    return validateResponse;
}

function* captureSelfieMigration({ document, gender, exchangeToken, guestFlow = false }) {
    const genericErrorMessage = i18n.get("genericError.business.withoutAmount");
    const unexpectedErrorMessage = i18n.get("global.unexpectedError");

    const hasCameraPermission = yield* checkCameraPermission();

    if (hasCameraPermission) {
        const doCaptureSelfieData = yield* doCaptureSelfie();

        const { maxAttemptsReached, hasImageError, selfieList, analysisSelfieList } = doCaptureSelfieData;

        if (maxAttemptsReached) {
            const imageErrorMessage = i18n.get("onboarding.captureSelfie.invalid");
            yield put({
                type: onboardingTypes.CAPTURE_SELFIE_MIGRATION_FAILURE,
                errorValidateIdentity: hasImageError ? imageErrorMessage : genericErrorMessage,
            });
        } else {
            // Validacion de la selfie en API con VU con reintentos
            const validateResponse = yield* doValidateSelfieMigration(
                selfieList,
                analysisSelfieList,
                document,
                gender,
                exchangeToken,
                guestFlow,
            );

            if (
                validateResponse.type === "W" ||
                validateResponse.type === "E" ||
                validateResponse.status !== 200 ||
                !validateResponse.type
            ) {
                const messageNoField = validateResponse.data.data?.NO_FIELD || null;
                const message = validateResponse.data.data?.message || null;

                const { code } = validateResponse.data;
                if (code === VU_ONBOARDING_INVALID_END_OPERATION) {
                    yield put(push(`/migration/stepScanDocument`));
                }

                yield put({
                    type: onboardingTypes.CAPTURE_SELFIE_MIGRATION_FAILURE,
                    errorValidateIdentity: messageNoField || message || unexpectedErrorMessage,
                });
            } else {
                if (guestFlow) {
                    gaEventTracker.triggerEvent(events.signerVuSuccess);
                } else {
                    gaEventTracker.triggerEvent(events.vuSuccess);
                }

                yield put({
                    type: onboardingTypes.CAPTURE_SELFIE_MIGRATION_SUCCESS,
                });
                yield put(push("/migration/StepCaptureSelfieSuccess"));
            }
        }
    } else {
        yield put({
            type: onboardingTypes.CAPTURE_SELFIE_MIGRATION_FAILURE,
        });
        // Si no fueron otorgados los permisos sobre la camara se muestra el dialog
        // consultando para que sean otorgados con posibilidad de ir al settings.
        const confirmValue = yield call(
            confirm,
            i18n.get("vuOnBoarding.cameraPermission.message"),
            i18n.get("vuOnBoarding.cameraPermission.ok"),
            i18n.get("vuOnBoarding.cameraPermission.cancel"),
            i18n.get("vuOnBoarding.cameraPermission.title"),
        );
        if (confirmValue === 1) {
            yield call(switchToSettings);
        }
    }
}

function* captureSelfieRequest({ document, gender, exchangeToken, guestFlow = false }) {
    const genericErrorMessage = i18n.get("genericError.business.withoutAmount");
    const unexpectedErrorMessage = i18n.get("global.unexpectedError");

    const hasCameraPermission = yield* checkCameraPermission();

    if (hasCameraPermission) {
        const onbSkipSelfie = config.getBoolean("onb.skipSelfie");
        let doCaptureSelfieData;
        if (onbSkipSelfie === true) {
            doCaptureSelfieData = {
                maxAttemptsReached: 0,
                hasImageError: false,
                selfieList: "selfieList",
                analysisSelfieList: "analysisSelfieList",
            };
        } else {
            console.log("captureSelfieRequest - pre");
            doCaptureSelfieData = yield* doCaptureSelfie();
            console.log("captureSelfieRequest - doCaptureSelfieData: ", doCaptureSelfieData);
        }

        const { maxAttemptsReached, hasImageError, selfieList, analysisSelfieList } = doCaptureSelfieData;

        if (maxAttemptsReached) {
            const imageErrorMessage = i18n.get("onboarding.captureSelfie.invalid");
            yield put({
                type: onboardingTypes.CAPTURE_SELFIE_FAILURE,
                errorValidateIdentity: hasImageError ? imageErrorMessage : genericErrorMessage,
            });
        } else {
            // Validacion de la selfie en API con VU con reintentos
            const validateResponse = yield* doValidateSelfie(
                selfieList,
                analysisSelfieList,
                document,
                gender,
                exchangeToken,
                guestFlow,
            );

            if (
                validateResponse.type === "W" ||
                validateResponse.type === "E" ||
                validateResponse.status !== 200 ||
                !validateResponse.type
            ) {
                const messageNoField = validateResponse.data.data?.NO_FIELD || null;
                const message = validateResponse.data.data?.message || null;

                const { code } = validateResponse.data;
                if (code === VU_ONBOARDING_INVALID_END_OPERATION) {
                    yield put(push(`/onboarding/stepScanDocument`));
                }

                yield put({
                    type: onboardingTypes.CAPTURE_SELFIE_FAILURE,
                    errorValidateIdentity: messageNoField || message || unexpectedErrorMessage,
                });
            } else {
                if (guestFlow) {
                    gaEventTracker.triggerEvent(events.signerVuSuccess);
                } else {
                    gaEventTracker.triggerEvent(events.vuSuccess);
                }

                yield put({
                    type: onboardingTypes.CAPTURE_SELFIE_SUCCESS,
                });
                yield put(push("/onboarding/StepCaptureSelfieSuccess"));
            }
        }
    } else {
        yield put({
            type: onboardingTypes.CAPTURE_SELFIE_FAILURE,
        });
        // Si no fueron otorgados los permisos sobre la camara se muestra el dialog
        // consultando para que sean otorgados con posibilidad de ir al settings.
        const confirmValue = yield call(
            confirm,
            i18n.get("vuOnBoarding.cameraPermission.message"),
            i18n.get("vuOnBoarding.cameraPermission.ok"),
            i18n.get("vuOnBoarding.cameraPermission.cancel"),
            i18n.get("vuOnBoarding.cameraPermission.title"),
        );
        if (confirmValue === 1) {
            yield call(switchToSettings);
        }
    }
}

function* verifyCodeSend({ verificationMethod, _userEmail, _userPhoneNumber, preferenceToken = false, businessName }) {
    const params = { verificationMethod, _userEmail, _userPhoneNumber, businessName };
    let response = null;
    if (preferenceToken === true) {
        response = yield call(settings.VerificationCodeSend, params);
    } else {
        response = yield call(onboarding.verifyCodeSend, params);
    }

    if (response && response.status === 200) {
        const { code = i18n.get("global.unexpectedError") } = response.data;

        const ticketStates = {
            COR000I: "OK",
        };

        if (code && ticketStates[code] === undefined) {
            yield put({ type: onboardingTypes.FAILURE_VERIFY_CODE_SEND });
            return;
        }
        yield put({
            type: onboardingTypes.SUCCESS_VERIFY_CODE_SEND,
            verifyCode: {
                type: "send",
                status: ticketStates[response.data.code],
                data: response.data,
            },
        });
    }
}

function* verifyCodeReceive({
    _code,
    verificationMethod,
    _userEmail,
    _userPhoneNumber,
    exchangeToken,
    guestFlow = false,
    preferenceToken = false,
}) {
    const params = { _code, verificationMethod, _userEmail, _userPhoneNumber, guestFlow };

    try {
        let response = null;
        if (preferenceToken === true) {
            response = yield call(settings.VerificationCodeReceive, params);
        } else {
            response = yield call(onboarding.verifyCodeReceive, params, exchangeToken);
        }
        if (response && response.status === 200) {
            const { code = i18n.get("global.unexpectedError") } = response.data;

            const ticketStates = {
                API508W: "INVALID_INVITATION_CODE",
                API509W: "INVITATION_CODE_EXPIRED",
                API510W: "INVITATION_CODE_ALREADY_USED",
                API511W: "INVITATION_CODE_CANCELED",
                COR000I: "OK",
            };

            if (code && ticketStates[code] === undefined) {
                yield put({ type: onboardingTypes.FAILURE_VERIFY_CODE_RECEIVE });
                return;
            }

            if (verificationMethod === "sms") {
                if (guestFlow) {
                    gaEventTracker.triggerEvent(events.firmRegistry2);
                } else {
                    gaEventTracker.triggerEvent(events.registry4);
                }
            }

            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CODE_RECEIVE,
                verifyCode: {
                    type: "receive",
                    status: ticketStates[response.data.code],
                    data: response.data,
                },
                email: _userEmail,
                mobileNumber: _userPhoneNumber,
            });
        } else {
            yield put({
                type: onboardingTypes.FAILURE_VERIFY_CODE_RECEIVE,
                verifyCode: {
                    type: "receive",
                    status: response?.data?.code,
                    data: response?.data,
                },
            });
            // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.FAILURE_VERIFY_CODE_RECEIVE,
            verifyCode: {
                type: "receive",
                status: "FAILURE",
                data: error,
            },
        });
    }
}

function* verifyCodeReceiveMigration({
    _code,
    verificationMethod,
    _userEmail,
    _userPhoneNumber,
    exchangeToken,
    guestFlow = false,
    preferenceToken = false,
}) {
    const params = { _code, verificationMethod, _userEmail, _userPhoneNumber, guestFlow };

    try {
        let response = null;
        if (preferenceToken === true) {
            response = yield call(settings.VerificationCodeReceive, params);
        } else {
            response = yield call(onboarding.verifyCodeReceiveMigration, params, exchangeToken);
        }
        if (response && response.status === 200) {
            const { code = i18n.get("global.unexpectedError") } = response.data;

            const ticketStates = {
                API508W: "INVALID_INVITATION_CODE",
                API509W: "INVITATION_CODE_EXPIRED",
                API510W: "INVITATION_CODE_ALREADY_USED",
                API511W: "INVITATION_CODE_CANCELED",
                COR000I: "OK",
            };

            if (code && ticketStates[code] === undefined) {
                yield put({ type: onboardingTypes.FAILURE_VERIFY_CODE_RECEIVE_MIGRATION });
                return;
            }

            if (verificationMethod === "sms") {
                if (guestFlow) {
                    gaEventTracker.triggerEvent(events.firmRegistry2);
                } else {
                    gaEventTracker.triggerEvent(events.registry4);
                }
            }

            yield put({
                type: onboardingTypes.SUCCESS_VERIFY_CODE_RECEIVE_MIGRATION,
                verifyCode: {
                    type: "receive",
                    status: ticketStates[response.data.code],
                    data: response.data,
                },
                email: _userEmail,
                mobileNumber: _userPhoneNumber,
            });
        } else {
            yield put({
                type: onboardingTypes.FAILURE_VERIFY_CODE_RECEIVE_MIGRATION,
                verifyCode: {
                    type: "receive",
                    status: response?.data?.code,
                    data: response?.data,
                },
            });
            // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.FAILURE_VERIFY_CODE_RECEIVE_MIGRATION,
            verifyCode: {
                type: "receive",
                status: "FAILURE",
                data: error,
            },
        });
    }
}

function* requestCreateUser({ userData, _exchangeToken }) {
    try {
        const response = yield call(onboarding.requestCreateUser, userData, _exchangeToken);

        if (response && response.status === 200) {
            const hasPasswordError = Object.keys(response?.data?.data || []).includes("password");

            if (response.data.code === "COR020W" && hasPasswordError) {
                yield put({
                    type: onboardingTypes.FAILURE_UNSECURE_PASSWORD,
                    errorMessage: response.data.data.password,
                });
                yield put(push("/onboarding/stepPassword"));
                return;
            }

            if (response.data.code !== "COR000I") {
                yield put({
                    type: onboardingTypes.FAILURE_CREATE_USER,
                    userCreationStatus: response.data.message,
                    errorMessage: "",
                });
                // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
            } else {
                yield put({
                    type: onboardingTypes.SUCCESS_CREATE_USER,
                    userCreationStatus: "SUCCESS",
                    errorMessage: "",
                    idEnvironment: response.data.data.idEnvironment,
                });
                yield put(push("/"));
            }
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.FAILURE_CREATE_USER,
            userCreationStatus: error?.data?.message,
            errorMessage: "",
        });
    }
}

function* acceptScheme({ idUser, idEnvironment }) {
    const response = yield call(onboarding.loginSignatureAccept, { idUser, idEnvironment });

    if (response && response.status === 200) {
        if (response.data.code !== "COR000I") {
            yield put({ type: onboardingTypes.FAILURE_CREATE_USER, userCreationStatus: response.data.message });
            // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
        } else {
            yield put({ type: onboardingTypes.SUCCESS_ACCEPT_SCHEME, userData: response.data?.data?.userData });
            yield put(push("/desktop"));
        }
    }
}

// Datos personales y regulatorios
function* requestSavePersonalData({ personalData, documentType, document, gender, _exchangeToken, guestFlow = false }) {
    try {
        const response = yield call(
            onboarding.requestSavePersonalData,
            { personalData, documentType, document, gender, guestFlow },
            _exchangeToken,
        );

        if (response && response.data?.code === "COR000I") {
            const { occupation, nationality, province, fatca, pep } = personalData;
            if (guestFlow) {
                gaEventTracker.triggerEvent(
                    events.firmRegistry4(nationality, occupation, province, fatca ? "si" : "no", pep ? "si" : "no"),
                );
            } else {
                gaEventTracker.triggerEvent(events.registry6(occupation, province, pep ? "si" : "no"));
            }

            yield put({ type: onboardingTypes.SUCCESS_SAVE_PERSONAL_DATA, personalDataStatus: "SUCCESS" });
        } else {
            yield put({
                type: onboardingTypes.FAILURE_SAVE_PERSONAL_DATA,
                personalDataStatus: response?.data?.message,
            });
            // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
        }
    } catch (e) {
        yield put({ type: onboardingTypes.FAILURE_SAVE_PERSONAL_DATA, personalDataStatus: e?.response?.data?.message });
    }
}

function* getPersonalData({ document, _exchangeToken }) {
    try {
        const response = yield call(onboarding.physicalPersonRead, { document }, _exchangeToken);

        if (response && response.data?.code === "COR000I") {
            yield put({ type: onboardingTypes.SUCCESS_GET_PERSONAL_DATA });
            yield put(push("/migration/stepUser"));
        } else {
            if (response.data?.code === "API613W") {
                yield put(push("/migration/stepPersonalData"));
            }
            yield put({
                type: onboardingTypes.FAILURE_GET_PERSONAL_DATA,
            });
        }
    } catch (e) {
        yield put({ type: onboardingTypes.FAILURE_GET_PERSONAL_DATA, personalDataStatus: e?.response?.data?.message });
    }
}

// function* requestSaveRegulatoryData({
//     document,
//     documentType,
//     gender,
//     regulatoryData,
//     _exchangeToken,
//     guestFlow = false,
// }) {
//     const response = yield call(
//         onboarding.requestSaveRegulatoryData,
//         {
//             document,
//             documentType,
//             gender,
//             regulatoryData,
//             guestFlow,
//         },
//         _exchangeToken,
//     );

//     if (response && response.status === 200) {
//         if (response.data.code !== "COR000I") {
//             yield put({
//                 type: onboardingTypes.FAILURE_SAVE_REGULATORY_DATA,
//                 regulatoryDataStatus: response.data.message,
//             });
//             // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
//         } else {
//             const { fatca, pep } = regulatoryData;
//             if (guestFlow) {
//                 gaEventTracker.triggerEvent(events.firmRegistry5(fatca ? "si" : "no", pep ? "si" : "no"));
//             } else {
//                 gaEventTracker.triggerEvent(events.registry7(pep ? "si" : "no"));
//             }
//             yield put({ type: onboardingTypes.SUCCESS_SAVE_REGULATORY_DATA, regulatoryDataStatus: "SUCCESS" });
//         }
//     }
// }

function* getUserRole() {
    try {
        const response = yield call(onboarding.getUserRole);

        if (response && response.data?.code === "COR000I") {
            yield put({ type: onboardingTypes.USER_ROLE_SUCCESS, userRoleRequest: response.data?.data });
        } else {
            yield put({
                type: onboardingTypes.USER_ROLE_FAILURE,
                userRoleRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.USER_ROLE_FAILURE,
            userRoleRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* verifyNosisCDA(documentData) {
    try {
        const response = yield call(onboarding.verifyNosisCDA, documentData);
        if (response && response.data?.code === "COR000I") {
            const nosisCDA = response.data?.data?.nosisCDA;
            const criteriasToFilter = [
                "CDA",
                "SCO_Vig",
                "VI_Inscrip_Afip_Antiguedad",
                "CI_Vig_Total_Monto",
                "CI_Vig_PeorSit",
                "JU_12m_RZ_Cant",
                "VI_Inscrip_GCIA_Condicion",
                "VI_Inscrip_IVA",
            ];
            const filteredAcceptanceCriteria = nosisCDA?.acceptanceCriteria.filter((criteria) =>
                criteriasToFilter.includes(criteria?.name),
            );
            yield put({
                type: onboardingTypes.NOSIS_CDA_SUCCESS,
                nosisCdaRequest: filteredAcceptanceCriteria,
                documentNumber: documentData.documentNumber,
                onboardingMainRole: response.data?.data?.onboardingMainRole,
            });
        } else {
            const errorMessage = response?.data?.message || response?.data?.data?.message;
            yield put({
                type: onboardingTypes.NOSIS_CDA_FAILURE,
                nosisCdaRequest: errorMessage,
                documentNumber: documentData.documentNumber,
            });
            yield put(notificationActions.showNotification(errorMessage, "error", ["environment"]));

            if (response.data?.code === "BAK589W") {
                yield call(delay, 2500);

                yield put(onboardingActions.resetNosisCda());

                yield put(replace("/"));
            }
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.NOSIS_CDA_FAILURE,
            nosisCdaRequest: error?.response?.data?.message || error?.data?.message,
            documentNumber: documentData.documentNumber,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* verifyOnbInvitationCode({ _code }) {
    try {
        const response = yield call(onboarding.verifyOnbInvitationCode, { _code });

        if (response && response.data?.code === "COR000I") {
            const {
                _exchangeToken,
                signer,
                userExists,
                userMigrated = false,
                multiEnvironment,
                redirectStepUser,
            } = response.data?.data;

            if (_exchangeToken) {
                yield put({
                    type: "enrollment/SAVE_EXCHANGE_TOKEN",
                    exchangeToken: _exchangeToken,
                });
            }

            yield put({
                type: onboardingTypes.ONB_INVITATION_CODE_SUCCESS,
                invitationCodeRequest: signer,
                userExists,
                userMigrated,
                multiEnvironment,
                redirectStepUser,
                invitationCode: _code,
                invitationSigner: signer,
            });
        } else {
            yield put({
                type: onboardingTypes.ONB_INVITATION_CODE_FAILURE,
                invitationCodeRequest: response?.data?.message || response?.data?.data?.message,
            });
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.ONB_INVITATION_CODE_FAILURE,
            invitationCodeRequest: error?.response?.data?.message || error?.data?.message,
        });
        // yield put(notificationActions.showNotification(response.data.message, "error", ["RegisterStep3Part1"]));
    }
}

function* handleVerifyUserCredentialsStep1Request({ _username, usernameSecret }) {
    const isUserValidationInEnrollment = true;
    const exchangeToken = yield select(enrollmentSelectors.getExchangeToken);
    let username = _username;

    // En caso de validación de credenciales para el flujo de firmante, el username se obtiene de la invitación
    if (username === "") {
        const invitationData = yield select(onboardingSelectors.getInvitationData);
        username = invitationData.cuil;
    }

    const response = yield call(
        session.loginStep1,
        isUserValidationInEnrollment,
        username,
        usernameSecret,
        exchangeToken,
    );

    if (response.data.code === "COR020W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["login"]));
        yield put({ type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_FAILURE });
    } else {
        const { _exchangeToken, _securitySeal, _securitySealAlt } = response.data.data;

        if (_exchangeToken) {
            yield put({
                type: loginTypes.SAVE_TOKEN_FOR_ONB_CREDENTIALS_VALIDATION,
                exchangeToken: _exchangeToken,
            });
        }

        yield put({
            type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_SUCCESS,
            securitySeal: _securitySeal,
            securitySealAlt: _securitySealAlt,
            username,
        });

        yield put(replace("/onboarding/stepVerifyPassword"));
    }
}

function* handleVerifyUserCredentialsStep2Request({ username, password }) {
    // se usan dos tokens diferentes, uno para el login y otro para el proceso de onboarding
    let _exchangeToken = yield select(loginSelectors.getExchangeToken);
    const onboardingWithInvitation = yield select(onboardingSelectors.getOnboardingWithInvitation);

    const response = yield call(session.oauth, username, password, _exchangeToken);

    const guestFlow = true;
    if (
        response.status === 400 ||
        response.data.code === "COR020W" ||
        response.data.code === "API019W" ||
        response.data.code === "API622E"
    ) {
        if (response.data.error_description === "API622E") {
            const error = new Error("Expired password");
            error.data = { code: response.data.error_description, _exchangeToken };
            throw error;
        }

        const message = response.data.message
            ? response.data.message
            : i18n.get("returnCode.API019W", "Usuario y/o contraseña no válido");
        yield put(notificationActions.showNotification(message, "error", ["login"]));
        yield put({ type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_FAILURE });
    } else if (onboardingWithInvitation) {
        const invitationCode = yield select(onboardingSelectors.getInvitationCode);
        _exchangeToken = yield select(enrollmentSelectors.getExchangeToken);
        const responseProcessInvitation = yield call(
            onboarding.processOnboardingEnrolledUser,
            {
                guestFlow,
                invitationCode,
            },
            _exchangeToken,
        );

        if (responseProcessInvitation.data.code === "COR020W") {
            yield put(notificationActions.showNotification(responseProcessInvitation.data.message, "error", ["login"]));
            yield put({ type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_FAILURE });
        } else {
            yield put({
                type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_SUCCESS,
            });

            yield put(replace("/onboarding/stepFinishUserValidation"));
        }
    } else {
        yield put(replace("/onboarding/stepVerifyUserCredentialsSuccess"));
    }
}

function* handleEnrolledUserSelectRole({ enrolledUserRole }) {
    const _exchangeToken = yield select(enrollmentSelectors.getExchangeToken);
    const role = enrolledUserRole;
    const email = yield select(onboardingSelectors.getEmail);
    const phone = yield select(onboardingSelectors.getMobileNumber);
    const cuil = yield select(onboardingSelectors.getUsername);
    const invitationCodeData = {
        productGroupId: null,
        email,
    };
    const responseProcessInvitation = yield call(
        onboarding.processOnboardingEnrolledUser,
        {
            cuil,
            role,
            invitationCodeData,
            phone,
        },
        _exchangeToken,
    );

    if (responseProcessInvitation.data.code === "COR020W") {
        yield put(notificationActions.showNotification(responseProcessInvitation.data.message, "error", ["login"]));
        yield put({ type: onboardingTypes.ENROLLED_USER_SELECT_ROLE_FAILURE });
    } else {
        yield put({
            type: onboardingTypes.ENROLLED_USER_SELECT_ROLE_SUCCESS,
        });

        yield put(replace("/onboarding/stepFinishUserValidation"));
    }
}

function* requestAcceptTermsAndConditionsPF(params) {
    try {
        const idEnvironment = yield select(enrollmentSelectors.getIdEnvironment);
        const body = { ...params, idEnvironment };
        const response = yield call(onboarding.requestTermsAndConditionsAcceptPF, body);

        const { type } = response;
        if (type === "W") {
            yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["login"]));
            yield put({ type: onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_FAILURE });
        } else {
            yield put({ type: onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_SUCCESS });
            yield put(replace("/"));
        }
    } catch (e) {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["login"]));
        yield put({ type: onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_FAILURE });
    }
}

function* requestAcceptTermsAndConditionsPJ({ category, idEnvironment }) {
    const enabledPayway = config.getBoolean("onboarding.payway.enable");
    try {
        const response = yield call(onboarding.requestTermsAndConditionsAcceptPJ, { category, idEnvironment });

        const { type } = response;
        if (type === "W") {
            yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["login"]));
            yield put({ type: onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_FAILURE });
        } else {
            yield put({ type: onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_SUCCESS });

            if (!enabledPayway) {
                yield put(replace("/"));
                gaEventTracker.triggerEvent(events.registry21);
            }

            if (enabledPayway) {
                const isPaywayAccepted = yield select(environmentSelectors.getIsPaywayAccepted);
                if (isPaywayAccepted === false) {
                    yield put(replace("/"));
                    gaEventTracker.triggerEvent(events.registry21);
                } else {
                    yield put(replace("/environment/stepPaywayFirst"));
                }
            }
        }
    } catch (e) {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["login"]));
        yield put({ type: onboardingTypes.ACCEPT_TERMS_AND_CONDITIONS_FINISH_STEP_FAILURE });
    }
}
function* handleVerifyUserCredentialsStep1MigrationRequest({ _username, usernameSecret }) {
    const isUserValidationInEnrollment = true;
    const exchangeToken = yield select(enrollmentSelectors.getExchangeToken);
    let username = _username;

    // En caso de validación de credenciales para el flujo de firmante, el username se obtiene de la invitación
    if (username === "") {
        const invitationData = yield select(onboardingSelectors.getInvitationData);
        username = invitationData.cuil;
    }

    const response = yield call(
        session.loginStep1,
        isUserValidationInEnrollment,
        username,
        usernameSecret,
        exchangeToken,
    );

    if (response.data.code === "COR020W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["login"]));
        yield put({ type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_MIGRATION_FAILURE });
    } else {
        const { _exchangeToken, _securitySeal, _securitySealAlt } = response.data.data;

        if (_exchangeToken) {
            yield put({
                type: loginTypes.SAVE_TOKEN_FOR_ONB_CREDENTIALS_VALIDATION,
                exchangeToken: _exchangeToken,
            });
        }

        yield put({
            type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_1_MIGRATION_SUCCESS,
            securitySeal: _securitySeal,
            securitySealAlt: _securitySealAlt,
            username,
        });

        yield put(replace("/migration/stepVerifyPassword"));
    }
}

function* handleVerifyUserCredentialsStep2MigrationRequest({ username, password }) {
    // se usan dos tokens diferentes, uno para el login y otro para el proceso de onboarding
    let _exchangeToken = yield select(loginSelectors.getExchangeToken);

    const response = yield call(session.oauth, username, password, _exchangeToken);

    if (
        response.status === 400 ||
        response.data.code === "COR020W" ||
        response.data.code === "API019W" ||
        response.data.code === "API622E"
    ) {
        if (response.data.error_description === "API622E") {
            const error = new Error("Expired password");
            error.data = { code: response.data.error_description, _exchangeToken };
            throw error;
        }

        const message = response.data.message
            ? response.data.message
            : i18n.get("returnCode.API019W", "Usuario y/o contraseña no válido");
        yield put(notificationActions.showNotification(message, "error", ["login"]));
        yield put({ type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_MIGRATION_FAILURE });
    } else {
        const invitationCode = yield select(onboardingSelectors.getInvitationCode);
        _exchangeToken = yield select(enrollmentSelectors.getExchangeToken);

        const responseProcessInvitation = yield call(
            onboarding.processOnboardingEnrolledUserMigration,
            {
                invitationCode,
            },
            _exchangeToken,
        );

        if (responseProcessInvitation.data.code === "COR020W") {
            yield put(notificationActions.showNotification(responseProcessInvitation.data.message, "error", ["login"]));
            yield put({ type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_MIGRATION_FAILURE });
        } else {
            const { idEnvironment } = responseProcessInvitation.data.data;
            yield put({
                type: onboardingTypes.VALIDATE_CREDENTIALS_STEP_2_MIGRATION_SUCCESS,
            });
            yield put({
                type: enrollmentTypes.SET_ID_ENVIRONMENT,
                idEnvironment,
            });

            yield put(replace("/migration/stepFinishUserValidation"));
        }
    }
}

function* requestUpdateMigratedUser({ userData, _exchangeToken }) {
    try {
        const response = yield call(onboarding.requestUpdateMigratedUser, userData, _exchangeToken);

        if (response && response.status === 200) {
            const hasPasswordError = Object.keys(response?.data?.data || []).includes("password");

            if (response.data.code === "COR020W" && hasPasswordError) {
                yield put({
                    type: onboardingTypes.FAILURE_UNSECURE_PASSWORD,
                    errorMessage: response.data.data.password,
                });
                yield put(push("/migration/stepPassword"));
                return;
            }

            if (response.data.code !== "COR000I") {
                yield put({
                    type: onboardingTypes.FAILURE_MIGRATION_UPDATE_USER,
                    userMigrationStatus: response.data.message,
                    errorMessage: "",
                });
            } else {
                const { idEnvironment } = response.data?.data;
                yield put({
                    type: enrollmentTypes.SET_ID_ENVIRONMENT,
                    idEnvironment,
                });
                yield put({
                    type: onboardingTypes.SUCCESS_MIGRATION_UPDATE_USER,
                    userMigrationStatus: "SUCCESS",
                    errorMessage: "",
                });
            }
        }
    } catch (error) {
        yield put({
            type: onboardingTypes.FAILURE_MIGRATION_UPDATE_USER,
            userCreationStatus: error?.response?.data?.message,
            errorMessage: "",
        });
    }
}

function* requestMigrationSavePersonalData({ personalData, document, gender, _exchangeToken }) {
    try {
        const response = yield call(
            onboarding.requestMigrationSavePersonalData,
            { personalData, document, gender },
            _exchangeToken,
        );

        if (response && response.data?.code === "COR000I") {
            yield put({ type: onboardingTypes.MIGRATION_SAVE_PERSONAL_DATA_SUCCESS, personalDataStatus: "SUCCESS" });
        } else {
            yield put({
                type: onboardingTypes.FAILURE_SAVE_PERSONAL_DATA,
                personalDataStatus: response?.data?.message,
            });
        }
    } catch (e) {
        yield put({
            type: onboardingTypes.MIGRATION_SAVE_PERSONAL_DATA_FAILURE,
            personalDataStatus: e?.response?.data?.message,
        });
    }
}
